import { memo } from 'react';
import clsx from 'clsx';
import styles from '../PdfRenderer.module.scss';
import type { Thumb } from '../hooks/usePdfThumbnails';

// Ширина рамки миниатюры. Должна совпадать с .thumbFrame в PdfRenderer.module.scss —
// по ней считается высота строки, а раскладка обязана быть предсказуема из JS.
const FRAME_W = 132;
// Вертикальная «обвязка» строки: padding 4 + 2, gap 4, строка с номером 11.
const ITEM_CHROME_H = 21;
// Пропорции A4 книжной — пока не измерена первая страница.
const DEFAULT_ASPECT = 1 / 1.414;

interface ThumbnailItemProps {
    page: number;
    thumb: Thumb | undefined;
    active: boolean;
    itemRef: (el: HTMLElement | null) => void;
    onSelectPage: (page: number) => void;
}

/**
 * Строка списка вынесена в memo намеренно: миниатюры доезжают по одной, и каждая
 * дергает setThumbs. Без memo любая готовая миниатюра перерисовывала бы все 300
 * кнопок разом — на скролле это выливается в пропущенные кадры. С memo коммит
 * трогает ровно ту строку, у которой изменился thumb (или подсветка активной).
 */
const ThumbnailItem = memo(function ThumbnailItem({
    page,
    thumb,
    active,
    itemRef,
    onSelectPage,
}: ThumbnailItemProps) {
    return (
        <button
            type="button"
            ref={itemRef}
            data-page={page}
            className={clsx(styles.thumbItem, active && styles['thumbItem--active'])}
            onClick={() => onSelectPage(page)}
            aria-label={`Страница ${page}`}
            aria-current={active ? 'page' : undefined}
        >
            <span className={styles.thumbFrame}>
                {thumb && <img className={styles.thumbImg} src={thumb.url} alt="" draggable={false} />}
            </span>
            <span className={styles.thumbLabel}>{page}</span>
        </button>
    );
});

interface ThumbnailsSidebarProps {
    totalPages: number;
    currentPage: number;
    thumbs: Map<number, Thumb>;
    /** width/height страницы 1 — задаёт высоту строк до рендера миниатюр. */
    aspect: number | null;
    itemRef: (el: HTMLElement | null) => void;
    onSelectPage: (page: number) => void;
}

/**
 * Лента миниатюр. Чисто презентационный компонент: что рендерить и когда —
 * решает usePdfThumbnails, сюда приезжает готовая карта object URL.
 *
 * Список не виртуализирован намеренно: строки дешёвые (пустой span или <img>),
 * а content-visibility: auto снимает с них раскладку и отрисовку за экраном.
 * Ключевое условие корректной работы content-visibility — заранее известная
 * высота строки, поэтому она считается здесь и уезжает в CSS-переменных.
 */
export function ThumbnailsSidebar({
    totalPages,
    currentPage,
    thumbs,
    aspect,
    itemRef,
    onSelectPage,
}: ThumbnailsSidebarProps) {
    const frameH = Math.round(FRAME_W / (aspect ?? DEFAULT_ASPECT));

    // Прокруткой до края ленты пользоваться нельзя: на 300 страницах это ~65 000px,
    // бегунок вырождается в минимальные ~17px и колесом до края ехать сотни щелчков.
    // Home/End дают прямой способ попасть на первую и последнюю страницу; сама
    // прокрутка подтянется следом (см. эффект по currentPage в usePdfThumbnails).
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Home') {
            e.preventDefault();
            onSelectPage(1);
        } else if (e.key === 'End') {
            e.preventDefault();
            onSelectPage(totalPages);
        }
    };

    return (
        <div
            className={styles.thumbsPanel}
            role="navigation"
            aria-label="Страницы документа"
            onKeyDown={handleKeyDown}
            style={{
                ['--thumb-frame-h' as string]: `${frameH}px`,
                ['--thumb-item-h' as string]: `${frameH + ITEM_CHROME_H}px`,
            }}
        >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <ThumbnailItem
                    key={page}
                    page={page}
                    thumb={thumbs.get(page)}
                    active={page === currentPage}
                    itemRef={itemRef}
                    onSelectPage={onSelectPage}
                />
            ))}
        </div>
    );
}
